import React, { useState, useEffect, useCallback } from "react";
import { Compass, Package, Pencil, Target } from "lucide-react";
import { api } from "../lib/api";
import { Section, InsightsGrid, ChatPanel, ModuleHeader } from "../components/intel/IntelKit";
import EditableValue from "../components/EditableValue";
import TargetEditorModal from "../components/TargetEditorModal";
import { getCurrentCustomer } from "../lib/customerSession";
import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import GrowthMentor from "../components/GrowthMentor";

// ---------------------------------------------------------------------------
// P0 ARCHITECTURE REDESIGN (2026-08) — Company Trajectory.
//
// C-ORE never decides the company's target horizon or target revenue.
// USER -> destination (the ONE canonical Strategic Target).
// C-ORE -> trajectory + gap + analysis + execution, derived FROM it.
//
// Removed entirely: the old system-defined 1yr/3yr/5yr/10yr
// LandingPoints grid, the multi-target FanChart, and the "5yr P50
// target"/"10yr P50"/"Focus horizon" KPI tiles that read from
// `company_parameters.revenue_target_{1,3,5,10}yr`. Those fields are left
// untouched in the DB (harmless) but are never read/written by this page
// again — see PRD.md for the full audit.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Trajectory status chip — same 4-state model as Command Center's
// Business Performance tab (`target_pacing.verdict`), never a 5th state
// invented here. "insufficient_data" covers both "no target yet" and
// "target exists but not enough financial history to pace it".
// ---------------------------------------------------------------------------
const VERDICT_STYLE = {
  on_track:         { label: "On Track",          cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  at_risk:          { label: "At Risk",           cls: "border-amber-200 bg-amber-50 text-amber-700" },
  off_track:        { label: "Off Track",         cls: "border-red-200 bg-red-50 text-red-700" },
  insufficient_data:{ label: "Insufficient Data", cls: "border-slate-200 bg-slate-50 text-slate-500" },
};

function fmtCroreOrDash(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(v >= 10_00_00_000 ? 1 : 2)}Cr`;
  if (v >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(1)}L`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

function fmtCr(cr) {
  if (cr === null || cr === undefined || Number.isNaN(Number(cr))) return "—";
  return `₹${Number(cr).toFixed(Number(cr) >= 10 ? 1 : 2)}Cr`;
}

// ---------------------------------------------------------------------------
// SECTION — Header strip: Last FY Actual · Strategic Target · Horizon ·
// Trajectory Status · Edit Target. P0 REDESIGN (2026-08, "remove current
// run-rate"): exactly TWO financial anchors on this page — the historical
// fact (Last FY Actual) and the founder-defined destination (Strategic
// Target). No Current Run Rate, no MTD×12, no derived "today" number.
// ---------------------------------------------------------------------------
function TrajectoryHeaderStrip({
  lastFYActualRup, strategicTarget, targetPacing, targetLoading, onEditTarget,
}) {
  const verdict = targetPacing
    ? (VERDICT_STYLE[targetPacing.verdict] || VERDICT_STYLE.insufficient_data)
    : (strategicTarget ? VERDICT_STYLE.insufficient_data : null);

  return (
    <div data-testid="proj-target-source-strip" className="flex flex-wrap items-center gap-2 text-[12px]">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-600" data-testid="proj-source-last-fy">
        <span className="text-slate-400">Last FY Actual</span>
        <span className="font-bold text-[#0F1B2D]">{lastFYActualRup != null ? fmtCroreOrDash(lastFYActualRup) : "—"}</span>
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 font-medium text-indigo-700" data-testid="proj-source-strategic-target">
        <Target size={12} />
        <span className="text-indigo-500">Strategic Target</span>
        {targetLoading
          ? <span className="text-indigo-400">Loading…</span>
          : strategicTarget
            ? <span className="font-bold">{fmtCr(strategicTarget.target_value)}</span>
            : <span className="text-indigo-400">Not set</span>}
      </span>
      {!targetLoading && strategicTarget && (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-white px-3 py-1.5 font-medium text-indigo-700" data-testid="proj-source-horizon">
          <span className="text-indigo-400">Horizon</span>
          <span className="font-bold">{strategicTarget.horizon_years >= 1 ? `${strategicTarget.horizon_years}y` : `${Math.round(strategicTarget.horizon_years * 12)}mo`}</span>
        </span>
      )}
      {verdict && (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-medium ${verdict.cls}`} data-testid="proj-pacing-verdict">
          {verdict.label}
          {targetPacing?.variance_pct != null && targetPacing.verdict !== "insufficient_data" && (
            <span className="opacity-70">· {targetPacing.variance_pct > 0 ? "+" : ""}{targetPacing.variance_pct}% vs plan</span>
          )}
        </span>
      )}
      <button
        type="button"
        onClick={onEditTarget}
        data-testid="proj-edit-target-btn"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700"
      >
        <Pencil size={11} />
        {strategicTarget ? "Edit Target" : "Set Target"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION — Trajectory chart: ONE deterministic path from Last FY Actual
// (the only historical anchor) to the Strategic Target, with dynamically-
// generated "C-ORE trajectory milestone" checkpoints (never a second
// target). Empty states are honest, never fabricated — no MTD/run-rate
// substitution when Last FY Actual isn't set.
// ---------------------------------------------------------------------------
function TrajectoryChart({ gap }) {
  if (!gap || gap.target_cr == null) {
    return (
      <div data-testid="proj-trajectory-empty-no-target" className="flex h-[200px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-center text-[12px] text-slate-500">
        <span className="text-slate-400 text-[18px] leading-none">—</span>
        <span>Set a Strategic Target above to see your trajectory.</span>
      </div>
    );
  }
  if (gap.current_cr == null) {
    return (
      <div data-testid="proj-trajectory-empty-no-data" className="flex h-[200px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-center text-[12px] text-slate-500">
        <span className="text-slate-400 text-[18px] leading-none">—</span>
        <span>Your Strategic Target is set at {fmtCr(gap.target_cr)}, but Last FY Actual hasn't been entered yet — set it below to see your trajectory.</span>
      </div>
    );
  }

  const W = 920, H = 220, PL = 44, PR = 20, PT = 20, PB = 34;
  const horizon = gap.horizon_years || 1;
  const points = [
    { x: 0, v: gap.current_cr, label: "Last FY Actual" },
    ...(gap.milestones || []).map((m) => ({
      x: Math.min(1, (m.year_index != null ? m.year_index : horizon) / horizon),
      v: m.revenue_cr,
      label: m.label,
    })),
  ];
  const yMaxRaw = Math.max(...points.map((p) => p.v), 1);
  const yMax = Math.ceil(yMaxRaw / 10) * 10 || Math.ceil(yMaxRaw) || 1;
  const yOf = (v) => H - PB - (Math.max(0, v) / yMax) * (H - PT - PB);
  const xOf = (frac) => PL + frac * (W - PL - PR);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(p.x)} ${yOf(p.v)}`).join(" ");
  const ticks = [yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax].map((v) => Math.round(v * 100) / 100);

  return (
    <div className="rounded-lg border border-blue-100 bg-[#F5F9FF] p-3" data-testid="proj-trajectory-chart">
      <div className="flex items-center justify-between px-1 pb-2 text-[10.5px] uppercase tracking-wider text-slate-500">
        <span>Trajectory · Last FY Actual → Strategic Target</span>
        <span>{gap.horizon_display || `${horizon}y`} horizon</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {ticks.map((g) => (
          <g key={g}>
            <line x1={PL} x2={W - PR} y1={yOf(g)} y2={yOf(g)} stroke="#dbeafe" strokeDasharray="2 4" />
            <text x={PL - 8} y={yOf(g) + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{g}Cr</text>
          </g>
        ))}
        <path d={path} fill="none" stroke="#2563eb" strokeWidth="2.4" />
        {points.map((p, i) => (
          <circle key={i} cx={xOf(p.x)} cy={yOf(p.v)} r="4.5" fill={i === points.length - 1 ? "#059669" : "#2563eb"} />
        ))}
        {points.map((p, i) => (
          <text key={`lbl-${i}`} x={xOf(p.x)} y={H - 10} textAnchor="middle" fontSize="10" fill="#475569">
            {i === points.length - 1 ? "Target" : p.label}
          </text>
        ))}
      </svg>
      <div className="mt-1 text-[10px] text-slate-400" data-testid="proj-trajectory-disclaimer">
        The blue dots are C-ORE trajectory milestones, not additional targets — the only target on this page is the Strategic Target you set above.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION — Gap stat tiles. Same `growth_engine.compute_gap()` numbers
// Growth Mentor's own Gap Analysis card shows below — one calculation,
// reused, never a second disagreeing figure.
// ---------------------------------------------------------------------------
function GapStatTile({ label, value, hint }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-[17px] font-bold tabular-nums text-[#0F1B2D]">{value ?? "—"}</div>
      {hint && <div className="mt-0.5 text-[11px] text-slate-400">{hint}</div>}
    </div>
  );
}

function GapStats({ gap }) {
  if (!gap || gap.target_cr == null) return null;
  return (
    <div data-testid="proj-gap-stats" className="mt-4 grid grid-cols-2 gap-3 desktop:grid-cols-4">
      <GapStatTile label="Revenue Gap" value={gap.gap_display} hint="Target − Last FY Actual" />
      <GapStatTile label="Required CAGR" value={gap.required_cagr_pct != null ? `${gap.required_cagr_pct}%` : "—"} hint="Year on year, to reach your target" />
      <GapStatTile label="Growth Multiple" value={gap.multiplier != null ? `${gap.multiplier}×` : "—"} hint="Target ÷ Last FY Actual" />
      <GapStatTile label="Trajectory Difficulty" value={gap.difficulty ? gap.difficulty[0].toUpperCase() + gap.difficulty.slice(1) : "—"} hint="Based on required CAGR" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION — Product Context (spec section 10, scoped/honest version).
// Only ever shows what genuinely exists — primary product text +
// whether inventory data is connected. Never fabricates SKU economics.
// ---------------------------------------------------------------------------
function ProductContextLine({ productCtx }) {
  if (!productCtx) return null;
  if (!productCtx.available) {
    return (
      <div data-testid="proj-product-context-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5 text-[12px] text-slate-500">
        <Package size={14} className="mt-0.5 shrink-0 text-slate-400" />
        <span>{productCtx.message || "Product-level trajectory unavailable — connect product/inventory data to enable."}</span>
      </div>
    );
  }
  return (
    <div data-testid="proj-product-context" className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-[12px] text-slate-600">
      <Package size={14} className="text-emerald-600" />
      {productCtx.primary_product && (
        <span><span className="font-semibold text-[#0F1B2D]">{productCtx.primary_product}</span>{productCtx.market_segment ? ` · ${productCtx.market_segment}` : ""}</span>
      )}
      {productCtx.has_inventory_data
        ? <span className="text-slate-400">· {productCtx.tracked_sku_count} SKU{productCtx.tracked_sku_count === 1 ? "" : "s"} tracked from inventory</span>
        : <span className="text-slate-400">· Connect inventory data for per-product trajectory signals</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION — Factor matrix (real data is roadmap; show honest empty state)
// ---------------------------------------------------------------------------
function FactorMatrix() {
  return (
    <div data-testid="proj-factor-matrix-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
      <span className="text-slate-400 text-[18px] leading-none">—</span>
      <span>The factor matrix appears once you have 8+ weeks of Tally history. It will show how revenue growth, gross margin, cash conversion, capacity utilisation and tech maturity move on your trajectory.</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module shell
// ---------------------------------------------------------------------------
export default function CompanyProjectile() {
  const { customerData, dataAvailable, isLoading, companyName, reload } = useCustomerData();
  const [strategicTarget, setStrategicTarget] = useState(null);
  const [trajectoryGap, setTrajectoryGap] = useState(null);
  const [targetPacing, setTargetPacing] = useState(null);
  const [targetLoading, setTargetLoading] = useState(true);
  const [productCtx, setProductCtx] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [targetVersion, setTargetVersion] = useState(0);

  // Single source of truth (P0 architecture redesign, 2026-08): re-fetches
  // the canonical strategic target + its deterministic gap/trajectory +
  // its pacing verdict in ONE call — the SAME reads every other module
  // (Command Center, Executive Briefing, CEO/CFO/CTO/CMO) traces back to
  // strategic_target_store / strategy_alignment. Deliberately does NOT
  // call the much heavier `/executive-intelligence/command-center`
  // endpoint just for a verdict badge — the backend computes the same
  // verdict here now. Called on mount, after Growth Mentor generates/
  // deploys a plan, and after the Target Editor saves directly.
  const refreshStrategicTarget = useCallback(() => {
    api.strategicTarget().then((res) => {
      if (res.ok) {
        setStrategicTarget(res.data?.target || null);
        setTrajectoryGap(res.data?.gap || null);
        setTargetPacing(res.data?.pacing || null);
      }
      setTargetLoading(false);
    });
  }, []);

  useEffect(() => { refreshStrategicTarget(); }, [refreshStrategicTarget]);
  useEffect(() => { api.productContext().then((res) => { if (res.ok) setProductCtx(res.data); }); }, []);

  const params = customerData?.params || {};
  // P0 REDESIGN (2026-08, "remove current run-rate"): Last FY Actual is
  // the ONLY historical anchor Company Trajectory reads — never
  // annualised MTD, never a trailing run-rate. If it isn't set, this is
  // simply null; there is no substituted number.
  const lastFYActualRup = params.revenue_last_fy_actual ? Number(params.revenue_last_fy_actual) : null;
  const customerId = (customerData?.customer_id || (() => { try { return getCurrentCustomer()?.id || null; } catch (_e) { return null; } })()) || null;
  const updatedAt = params.params_updated_at || {};

  const handleTargetSaved = (target) => {
    setStrategicTarget(target);
    setTargetVersion((v) => v + 1);
    refreshStrategicTarget();
  };

  return (
    <div data-testid="proj-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
        <ModuleHeader
          overline="Company Trajectory"
          title={`Where ${companyName || "your company"} is headed`}
          right={<><Compass size={13} className="text-emerald-600" />You decide the destination — C-ORE plots the path</>}
        />

        <TrajectoryHeaderStrip
          lastFYActualRup={lastFYActualRup}
          strategicTarget={strategicTarget}
          targetPacing={targetPacing}
          targetLoading={targetLoading}
          onEditTarget={() => setEditorOpen(true)}
        />

        {/* Last FY Actual — the ONE historical fact this page reads. */}
        <div className="flex items-center gap-2 text-[12.5px] text-slate-500">
          <span>Last completed FY actual revenue:</span>
          <EditableValue
            testId="proj-last-fy-edit"
            displayValue={lastFYActualRup != null ? fmtCroreOrDash(lastFYActualRup) : "—"}
            rawValue={lastFYActualRup}
            paramKey="revenue_last_fy_actual"
            paramLabel="Last Completed FY Actual"
            customerId={customerId}
            transformIn={(v) => (v ? v / 1_00_00_000 : "")}
            transformOut={(v) => (v === "" ? null : Math.round(Number(v) * 1_00_00_000))}
            lastUpdatedIso={updatedAt.revenue_last_fy_actual}
            onSaved={reload}
            inputPlaceholder="18.2"
          />
        </div>

        <ProductContextLine productCtx={productCtx} />

        {/* Trajectory + gap analysis */}
        <Section testid="proj-trajectory" title="Trajectory" subtitle="Last FY Actual → your Strategic Target · C-ORE trajectory milestones, not additional targets">
          <TrajectoryChart gap={trajectoryGap} />
          <GapStats gap={trajectoryGap} />
        </Section>

        {/* Factor matrix */}
        <Section testid="proj-factors" title="Factor matrix" subtitle="What moves the trajectory · green = positive, amber = caution, red = risk">
          <FactorMatrix />
        </Section>

        {/* Growth Mentor — full AI plan generator, reads/writes the SAME canonical target */}
        <GrowthMentor
          customerId={customerId}
          defaultTargetCr={strategicTarget ? Number(strategicTarget.target_value) : (lastFYActualRup ? +((lastFYActualRup / 1_00_00_000) * 3).toFixed(2) : 18.2)}
          defaultHorizon={strategicTarget ? Number(strategicTarget.horizon_years) : 3}
          onTargetChanged={refreshStrategicTarget}
          externalRefreshKey={targetVersion}
        />

        {/* AI insights */}
        <InsightsGrid
          subtitle="What your Trajectory advisor would tell you today"
          fetchFn={(force) => api.aiInsightsGenerate("ceo", api.projectileInsights, force)}
          testidPrefix="proj"
          dataAvailable={dataAvailable}
        />

        {/* Ask C-ORE */}
        <ChatPanel
          title="Ask your Trajectory advisor"
          subtitle="Your target · gap · trajectory · Hindi or English"
          placeholder="Ask about growth path, capex, or risks…"
          sendFn={api.projectileChat}
          testidPrefix="proj"
          emptyHint="Ask any long-term question. Try: 'What is my required growth rate?' or 'When should I open plant 2?'"
        />
      </div>

      <TargetEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTarget={strategicTarget}
        onSaved={handleTargetSaved}
      />
    </div>
  );
}
